import { ref } from 'vue'
import { defineStore } from 'pinia'
import axios from 'axios'
import { config } from '@/config'

const api = axios.create({ baseURL: config.apiUrl + '/api/activity' })

export interface ActivityLine {
  uuid: string
  created_on: string
  category: 'access' | 'data' | 'report' | 'system'
  action: string
  user_name: string | null
  entity_type: string | null
  entity_uuid: string | null
  entity_label: string | null
  changes: string | null // JSON text: {field: [before, after]} or {field: value}
  meta: string | null
  device_code: string | null
  ip: string | null
}

export interface ActivityFilter {
  category?: string
  action?: string
  q?: string
  date_from?: string
  date_to?: string
}

/** Fire and forget: a failed page-view line must never disturb navigation. */
export function logPageView(pageName: string, routePath: string) {
  if (!localStorage.getItem('authTicket')) return
  api.post('/page-view', { page_name: pageName, route_path: routePath }).catch(() => {})
}

/** "field: before → after" lines from a stored changes value, tolerant of any shape */
export function describeChanges(changes: string | null): string[] {
  if (!changes) return []
  try {
    const parsed = JSON.parse(changes)
    return Object.entries(parsed).map(([field, value]) =>
      Array.isArray(value) ? `${field}: ${value[0] ?? '∅'} → ${value[1] ?? '∅'}` : `${field}: ${value ?? '∅'}`
    )
  } catch {
    return [changes]
  }
}

export const useActivityStore = defineStore('activity', () => {
  const lines = ref<ActivityLine[]>([])
  const count = ref(0)
  const loading = ref(false)
  const error = ref('')

  async function load(filter: ActivityFilter, offset = 0, limit = 100) {
    loading.value = true
    error.value = ''
    try {
      const params: Record<string, string | number> = { limit, offset }
      for (const [key, value] of Object.entries(filter)) if (value) params[key] = value
      const { data } = await api.get('', { params })
      lines.value = offset ? [...lines.value, ...data.data] : data.data
      count.value = data.count
    } catch (e: any) {
      error.value =
        (typeof e?.response?.data === 'string' && e.response.data) || 'Could not load the activity log.'
    } finally {
      loading.value = false
    }
  }

  return { lines, count, loading, error, load }
})
